import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CornerDownLeftIcon, PackageIcon, PlusIcon, SearchIcon, ShoppingBagIcon, UserIcon } from 'lucide-react';
import { useAdmin } from '../../AdminContext';
import { fetchOrders } from '../../adminApi';
import { useCustomers } from '../../customers/useCustomers';
import { contactHint, customerName } from '../../customers/customerRules';
import { orderBadge, orderNumber } from '../../orders/orderLabels';
import { money } from '../../../core/format/currency';
import type { AdminOrderSummary } from '../../../core/types/adminRequests';
import { ease } from '../../../core/format/motion';
import type { IconComponent } from '../../../ui/icon';
import { sectionFor, type AdminNavItem } from '../../adminNav';
import { useModalLock } from './useModalLock';
import { useCan } from '../../useCan';
type Props = {
  open: boolean;
  onClose: () => void;
  items: AdminNavItem[];
  pageRef: RefObject<HTMLElement | null>;
};
type Hit = {
  key: string;
  label: string;
  hint: string;
  to: string;
  icon: IconComponent;
};

/** Handelingen die Sarah vaak zoekt; staan ook leeg al klaar. */
const actions: Hit[] = [{
  key: 'a-bon',
  label: 'Losse bon maken',
  hint: 'Kassa',
  to: '/be/kassa',
  icon: PlusIcon
}, {
  key: 'a-inkoop',
  label: 'Inkoopfactuur boeken',
  hint: 'Voorraad',
  to: '/be/voorraad?tab=inkoop',
  icon: PlusIcon
}, {
  key: 'a-product',
  label: 'Nieuw product',
  hint: 'Producten',
  to: '/be/producten/nieuw',
  icon: PlusIcon
}, {
  key: 'a-bon-cadeau',
  label: 'Nieuwe cadeaubon',
  hint: 'Cadeaubonnen',
  to: '/be/cadeaubonnen/nieuw',
  icon: PlusIcon
}, {
  key: 'a-artikel',
  label: 'Nieuw artikel',
  hint: 'Journal',
  to: '/be/journal/nieuw',
  icon: PlusIcon
}, {
  key: 'a-post',
  label: 'Post of flyer maken',
  hint: 'Ontwerpen',
  to: '/be/ontwerpen/nieuw',
  icon: PlusIcon
}];

/** Hoeveel klanten, bestellingen of producten per zoekopdracht; meer is scrollen in plaats van vinden. */
const LIMIT = 5;
/** Bestellingen zoekt de server (op nummer, naam en e-mail); pas vanaf zoveel tekens, en na even stil typen. */
const ORDER_MIN_CHARS = 2;
const ORDER_DELAY_MS = 250;
type OrderSearch = {
  term: string;
  items: AdminOrderSummary[];
  status: 'idle' | 'loading' | 'done' | 'error';
};

/**
 * Bestellingen zoeken via de server (GET /admin/orders?q=): de lijst is te groot om in de browser
 * te laden. Kort wachten na het typen, en een oudere vraag afbreken zodra er een nieuwe is.
 */
function useOrderSearch(raw: string): OrderSearch {
  const term = raw.trim();
  const [state, setState] = useState<OrderSearch>({
    term: '',
    items: [],
    status: 'idle'
  });
  useEffect(() => {
    if (term.length < ORDER_MIN_CHARS) {
      setState({
        term,
        items: [],
        status: 'idle'
      });
      return;
    }
    setState(s => ({
      ...s,
      term,
      status: 'loading'
    }));
    const controller = new AbortController();
    const t = window.setTimeout(() => {
      fetchOrders({
        q: term
      }, {
        signal: controller.signal
      }).then(page => setState({
        term,
        items: page.items.slice(0, LIMIT),
        status: 'done'
      })).catch(() => {
        if (!controller.signal.aborted) setState({
          term,
          items: [],
          status: 'error'
        });
      });
    }, ORDER_DELAY_MS);
    return () => {
      window.clearTimeout(t);
      controller.abort();
    };
  }, [term]);
  return state;
}
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Zoeken in het hele beheer (⌘K / Ctrl K, of de zoekknop bovenin): onderdelen, handelingen,
 * klanten en producten in één lijst. Pijltjes kiezen, Enter opent. Zo hoeft Sarah het menu
 * niet te kennen om iets te vinden.
 */
export function CommandMenu({
  open,
  onClose,
  items,
  pageRef
}: Props) {
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  useModalLock(open, panelRef, pageRef, onClose);
  return <AnimatePresence data-id="element-5493" data-mp="2fwxzq9glrqci">
      {open && <div className="fixed inset-0 z-overlay flex items-start justify-center px-4 pt-[12vh]" data-id="element-5494" data-mp="2ia1ru09vv9sr">
          <motion.button type="button" aria-label="Zoeken sluiten" tabIndex={-1} onClick={onClose} className="absolute inset-0 bg-ink/30" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease
      }} data-id="element-5495" data-mp="2mb3l80h4crmt" />
          <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-label="Zoeken in het beheer" className="relative w-full max-w-xl overflow-hidden rounded-xl border border-line bg-white shadow-float" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96,
        y: -6
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.98
      }} transition={{
        duration: 0.16,
        ease
      }} data-id="element-5496" data-mp="1yjps3sb58s0s">
            <Search items={items} onClose={onClose} data-id="element-5497" data-mp="1dben4kx9bup4" />
          </motion.div>
        </div>}
    </AnimatePresence>;
}

/** Alleen getekend als het venster open is: dan pas klanten laden. */
function Search({
  items,
  onClose
}: {
  items: AdminNavItem[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const {
    catalog
  } = useAdmin();
  // Een collega zoekt alleen onderdelen die ze mag zien: geen klantenlijst, bestellingen of
  // producten (die schermen zijn van Sarah). Dan ook niets ophalen.
  const {
    canAll
  } = useCan();
  const findsCustomers = canAll('customers.view');
  const findsOrders = canAll('orders.view');
  const findsProducts = canAll('catalog.edit');
  const {
    customers
  } = useCustomers(findsCustomers);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const orderSearch = useOrderSearch(findsOrders ? q : '');
  const groups = useMemo(() => {
    const term = norm(q.trim());
    const match = (...parts: (string | undefined)[]) => !term || parts.some(p => p && norm(p).includes(term));
    const sections: Hit[] = items.filter(n => match(n.label, n.group)).map(n => ({
      key: `s-${n.key}`,
      label: n.label,
      hint: n.group === 'Beheer' ? 'Instellingen' : n.group,
      to: n.to,
      icon: n.icon
    }));
    // Snelle acties naar een onderdeel dat je niet mag openen, staan er niet tussen.
    const acts = actions.filter(a => {
      const path = a.to.split('?')[0];
      return sectionFor(path, items) !== undefined && match(a.label, a.hint);
    });
    const people: Hit[] = term ? customers.filter(c => match(customerName(c), c.email, c.phone ?? '')).slice(0, LIMIT).map(c => ({
      key: `c-${c.id}`,
      label: customerName(c),
      hint: contactHint(c),
      to: `/be/klanten/${c.id}`,
      icon: UserIcon
    })) : [];
    const products: Hit[] = term && findsProducts ? catalog.products.filter(p => match(p.name, p.brand)).slice(0, LIMIT).map(p => ({
      key: `p-${p.id}`,
      label: p.name,
      hint: p.brand,
      to: `/be/producten/${p.id}`,
      icon: PackageIcon
    })) : [];
    const orders: Hit[] = orderSearch.items.map(o => ({
      key: `o-${o.id}`,
      label: `${orderNumber(o)} · ${o.customerName || o.email}`,
      hint: `${money(o.totalCents)} · ${orderBadge(o).label}`,
      to: `/be/bestellingen/${o.id}`,
      icon: ShoppingBagIcon
    }));
    // Lijkt het op een bestelnummer (vooral cijfers), dan bestellingen bovenaan.
    const numberLike = /^#?\s*[\w-]*\d{3,}/.test(q.trim());
    const orderGroup = {
      title: 'Bestellingen',
      hits: orders
    };
    return [...(numberLike ? [orderGroup] : []), {
      title: 'Klanten',
      hits: people
    }, ...(numberLike ? [] : [orderGroup]), {
      title: 'Producten',
      hits: products
    }, {
      title: 'Doen',
      hits: acts
    }, {
      title: 'Onderdelen',
      hits: sections
    }].filter(g => g.hits.length > 0);
  }, [q, items, customers, catalog.products, orderSearch.items]);
  const flat = groups.flatMap(g => g.hits);
  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({
      block: 'nearest'
    });
  }, [active]);
  const go = (h: Hit | undefined) => {
    if (!h) return;
    onClose();
    navigate(h.to);
  };
  let index = -1;
  return <>
      <label className="flex items-center gap-3 border-b border-line px-4" data-id="element-5498" data-mp="mde6szmlspi0">
        <SearchIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-5499" data-mp="r9w9ltydidrp" />
        <span className="sr-only" data-id="element-5500" data-mp="w7xqq9c8o30j">Zoek een klant, bestelling, product of onderdeel</span>
        <input autoFocus role="combobox" aria-expanded="true" aria-controls="command-list" aria-activedescendant={flat[active] ? `cmd-${flat[active].key}` : undefined} value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActive(i => Math.min(i + 1, flat.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActive(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
          e.preventDefault();
          go(flat[active]);
        }
      }} placeholder="Klant, bestelnummer, product of onderdeel…" className="h-14 w-full bg-transparent text-[16px] text-ink placeholder:text-muted focus:outline-none sm:text-[15px]" data-id="element-5501" data-mp="10cdxectofegt" />
        <kbd className="hidden shrink-0 rounded border border-line px-1.5 py-0.5 text-[11px] text-muted sm:block" data-id="element-5502" data-mp="3dilpz4pr3w99">Esc</kbd>
      </label>

      <p className="sr-only" role="status" aria-live="polite" data-id="element-5503" data-mp="34hdz37pkdxp7">
        {orderSearch.status === 'loading' ? 'Bestellingen zoeken…' : orderSearch.status === 'error' ? 'Bestellingen zoeken lukte niet.' : ''}
      </p>
      {flat.length === 0 ? <p className="px-4 py-8 text-center text-[14px] text-muted" data-id="element-5504" data-mp="1t0dzdr0w0gl5">
          {orderSearch.status === 'loading' ? 'Zoeken…' : `Niets gevonden voor “${q}”.`}
        </p> : <ul id="command-list" ref={listRef} role="listbox" aria-label="Resultaten" className="max-h-[50vh] overflow-y-auto py-2" data-id="element-5505" data-mp="2euq9g16yvwva">
          {groups.map(g => <li key={g.title} role="presentation" data-id="element-5506" data-mp="2nun4ckjy9rys">
              <p className="px-4 pb-1 pt-2 text-[12px] text-muted" aria-hidden="true" data-id="element-5507" data-mp="2nstxp59c77qs">
                {g.title}
              </p>
              <ul role="presentation" data-id="element-5508" data-mp="32l45s3wl4hvo">
                {g.hits.map(h => {
            index += 1;
            const i = index;
            const on = i === active;
            return <li key={h.key} id={`cmd-${h.key}`} role="option" aria-selected={on} onMouseMove={() => setActive(i)} onClick={() => go(h)} className={`mx-2 flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-[14px] ${on ? 'bg-sand text-ink' : 'text-ink'}`} data-id="element-5509" data-mp="36l4i9j04ij03">
                      <h.icon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-5510" data-mp="3sss5sjbhxsl0" />
                      <span className="min-w-0 flex-1 truncate" data-id="element-5511" data-mp="3f1n2n78x6i5x">{h.label}</span>
                      <span className="hidden max-w-[40%] shrink-0 truncate text-[12px] text-muted sm:block" data-id="element-5512" data-mp="3j25taxz2ti6j">{h.hint}</span>
                      {on && <CornerDownLeftIcon className="h-3.5 w-3.5 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-5513" data-mp="355sanlit3aqh" />}
                    </li>;
          })}
              </ul>
            </li>)}
        </ul>}
      {orderSearch.status === 'error' && <p className="border-t border-line px-4 py-2 text-[12px] text-clay" data-id="element-5514" data-mp="306p8q9y4hkmq">Bestellingen zoeken lukte niet. Controleer je verbinding.</p>}
    </>;
}